import {
  CaptureApiClient,
  RegistrationState,
  RevisionType,
  type ProjectApi,
  type RegistrationRevision,
} from "@faro-lotv/service-wires";

type ApplyRevisionParams = {
  /**
   * The revision to apply to draft.
   */
  revision: RegistrationRevision;

  /**
   * The API client to apply the revision.
   */
  projectApiClient: ProjectApi;
};

/**
 * Applies a revision to draft, "saving" the changes.
 * Once the Draft is published, the changes will be available on the Live view.
 *
 * @param params The parameters for applying the revision.
 * @returns The applied revision.
 */
export async function applyRevision({
  revision,
  projectApiClient,
}: ApplyRevisionParams): Promise<RegistrationRevision> {
  let rev = revision;

  if (
    rev.revisionType === RevisionType.draft ||
    rev.state === RegistrationState.merged ||
    rev.state === RegistrationState.canceled
  ) {
    // Already applied or canceled, nothing to do
    return rev;
  }

  async function updateState(state: RegistrationState): Promise<void> {
    rev = await projectApiClient.updateRegistrationRevision({
      registrationRevisionId: revision.id,
      state,
      // Prevent automatic deletion of elements in the revision if they were not modified
      deleteImplicitly: false,
    });
  }

  if (rev.state === RegistrationState.userModified) {
    // User modified can't be set to registered directly
    await updateState(RegistrationState.cloudRegistrationStarted);
  }

  if (rev.state !== RegistrationState.registered) {
    // Status must be registered before the revision can be merged
    await updateState(RegistrationState.registered);
  }

  // Merge the revision to draft, so the other clients (registration backend, dashboard) can see the changes
  await projectApiClient.applyRegistrationRevision(revision.id);
  rev = {
    ...rev,
    state: RegistrationState.merged,
  };

  // Workaround to make the legacy registration workflow compatible with the new "draft is truth" registration workflow.
  // For the data management a reviewed registration with the legacy workflow looks the same as an not reviewed registration
  // with the new workflow. That's because in the legacy workflow we don't differentiate between frontend changes (which
  // are user approved) and backend changes. In the legacy workflow the user is reviewing the registration results in a
  // backend revision and approves them there. In the new workflow this done on the draft.
  // The data management does consider all current changes reviewed, if the last change is coming from a registration
  // frontend revision. So this workaround creates an empty frontend revision and merges it, after revisions that were
  // created with the legacy workflow are merged. This indicates to data management that the changes are already approved.
  if (rev.createdByClient !== CaptureApiClient.registrationFrontend) {
    let dummyRevision = await projectApiClient.createRegistrationRevision(
      [],
      [],
      CaptureApiClient.registrationFrontend,
    );
    dummyRevision = await projectApiClient.updateRegistrationRevision({
      registrationRevisionId: dummyRevision.id,
      state: RegistrationState.registered,
      deleteImplicitly: false,
    });
    await projectApiClient.applyRegistrationRevision(dummyRevision.id);
  }

  return rev;
}
